import { BrowserRouter, Routes, Route, useNavigate, Navigate } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import PrivateRoute from "../components/PrivateRoute";
import Layout from "../components/Layout";
import AdminRoute from "../components/AdminRoute";
import GlobalAdminRoute from "../components/GlobalAdminRoute";
import SuperAdminRoute from "../components/SuperAdminRoute";
import InitialRoute from "../components/InitialRoute";
import { useChatContext } from "../context/useChatContext.js";

import Login from "../pages/Login";
import ForgotPassword from "../pages/ForgotPassword";
import ResetPassword from "../pages/ResetPassword";
import WhatsAppTerms from "../pages/WhatsAppTerms";
import { LOGOUT_EVENT } from "../utils/auth";
import "./AppRoutes.css";

const PUBLIC_AUTH_PATHS = new Set(['/login', '/forgot-password', '/reset-password']);

const isPublicAuthPath = (pathname) => {
  const normalizedPath = String(pathname || '').replace(/\/+$/, '') || '/';
  return PUBLIC_AUTH_PATHS.has(normalizedPath);
};

const Dashboard = lazy(() => import("../pages/Dashboard"));
const Attendants = lazy(() => import("../pages/Attendants"));
const Clients = lazy(() => import("../pages/Customers"));
const ChatPage = lazy(() => import("../pages/ChatPage"));
const InternalChat = lazy(() => import("../pages/InternalChat"));
const Queues = lazy(() => import("../pages/Queues"));
const CompanySettings = lazy(() => import("../pages/CompanySettings"));
const ConversationsPage = lazy(() => import("../pages/ConversationsPage"));
const CompanyRegister = lazy(() => import("../pages/CompanyRegister"));
const Reports = lazy(() => import("../pages/Reports"));
const ConversationAnalytics = lazy(() => import("../pages/analytics/ConversationAnalytics"));
const SatisfactionAnalytics = lazy(() => import("../pages/analytics/SatisfactionAnalytics"));
const MessageTrafficAnalytics = lazy(() => import("../pages/analytics/MessageTrafficAnalytics"));
const NoticeBoard = lazy(() => import("../pages/NoticeBoard"));
const GroupChat = lazy(() => import("../pages/GroupChat"));
const ClientUpdatesAdmin = lazy(() => import("../pages/ClientUpdatesAdmin"));
const ClientUpdatesBoard = lazy(() => import("../pages/ClientUpdatesBoard"));
const WorkflowBuilder = lazy(() => import("../pages/WorkflowBuilder"));
const WorkflowActionsAdmin = lazy(() => import("../pages/WorkflowActionsAdmin"));
const SendMessageWebhooksAdmin = lazy(() => import("../pages/SendMessageWebhooksAdmin"));
const WhatsApps = lazy(() => import("../pages/WhatsApps"));
const Transmissions = lazy(() => import("../pages/Transmissions"));

function RouteFallback() {
  return (
    <div className="route-loading-state" aria-busy="true" aria-live="polite">
      <div className="route-loading-state__panel">
        <span className="route-loading-state__spinner" aria-hidden="true" />
        <span>Carregando tela...</span>
      </div>
    </div>
  );
}

function LazyRoute({ children }) {
  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
}

function GroupsDisabledRoute() {
  return (
    <div className="route-loading-state" aria-live="polite">
      <div className="route-loading-state__panel">
        <span>Grupos desabilitados para esta empresa.</span>
      </div>
    </div>
  );
}

function GroupsRouteGate() {
  const { hasCompany, isAuthReady, companySettings } = useChatContext();

  if (!isAuthReady || (hasCompany && companySettings === null)) {
    return <RouteFallback />;
  }

  if (hasCompany && !companySettings?.groups_enabled) {
    return <GroupsDisabledRoute />;
  }

  return (
    <LazyRoute>
      <GroupChat />
    </LazyRoute>
  );
}

function TaskBlueDisabledRoute({ message = "Task Blue indisponível para esta empresa." }) {
  return (
    <div className="route-loading-state" aria-live="polite">
      <div className="route-loading-state__panel">
        <span>{message}</span>
      </div>
    </div>
  );
}

function TaskBlueRouteGate() {
  const { hasCompany, isAuthReady, companySettings, attendantData, isGlobalAdmin } = useChatContext();

  if (!isAuthReady || (hasCompany && companySettings === null)) {
    return <RouteFallback />;
  }

  if (!hasCompany || !companySettings?.transmission_enabled) {
    return <TaskBlueDisabledRoute />;
  }

  if (!isGlobalAdmin && !attendantData?.can_access_taskblue) {
    return <TaskBlueDisabledRoute message="Você não tem permissão para acessar Task Blue." />;
  }

  return (
    <LazyRoute>
      <Transmissions />
    </LazyRoute>
  );
}

function LogoutRedirect() {
  const navigate = useNavigate();

  useEffect(() => {
    const handleLogout = () => {
      if (isPublicAuthPath(window.location?.pathname)) {
        return;
      }
      // Força sempre a tela de login após logout/expiração
      navigate("/login", { replace: true });
    };

    window.addEventListener(LOGOUT_EVENT, handleLogout);
    return () => window.removeEventListener(LOGOUT_EVENT, handleLogout);
  }, [navigate]);

  return null;
}

export default function AppRoutes() {
  return (
    <BrowserRouter>
      <LogoutRedirect />
      <Routes>
        {/* Ponto de entrada protegido que decide a tela inicial conforme perfil */}
        <Route path="/" element={<InitialRoute />} />

        {/* Página de login sem header */}
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Rotas protegidas sem layout (útil para telas isoladas) */}
        <Route
          path="/terms"
          element={
            <PrivateRoute>
              <WhatsAppTerms />
            </PrivateRoute>
          }
        />

        {/* Rotas protegidas com Header */}
        <Route
          element={
            <PrivateRoute>
              <LazyRoute>
                <Layout />
              </LazyRoute>
            </PrivateRoute>
          }
        >
          <Route path="/dashboard" element={<LazyRoute><Dashboard /></LazyRoute>} />
          <Route
            path="attendants"
            element={
              <AdminRoute>
                <LazyRoute>
                  <Attendants />
                </LazyRoute>
              </AdminRoute>
            }
          />
          <Route path="contacts" element={<LazyRoute><Clients /></LazyRoute>} />

          <Route path="clients" element={<Navigate to="/contacts" replace />} />

          <Route path="updates" element={<LazyRoute><ClientUpdatesBoard /></LazyRoute>} />

          <Route
            path="roles"
            element={
              <Navigate to="/" replace />
            }
          />
          <Route
            path="queues"
            element={
              <AdminRoute>
                <LazyRoute>
                  <Queues />
                </LazyRoute>
              </AdminRoute>
            }
          />
          <Route path="teams" element={<Navigate to="/queues" replace />} />
          <Route path="chatpage" element={<LazyRoute><ChatPage /></LazyRoute>} />
          <Route path="whatsapps" element={<LazyRoute><WhatsApps /></LazyRoute>} />
          <Route path="transmissions" element={<TaskBlueRouteGate />} />
          <Route
            path="groups"
            element={<GroupsRouteGate />}
          />
          <Route path="internal-chat" element={<LazyRoute><InternalChat /></LazyRoute>} />
          <Route
            path="companySettings"
            element={
              <AdminRoute>
                <LazyRoute>
                  <CompanySettings />
                </LazyRoute>
              </AdminRoute>
            }
          />
          <Route
            path="workflows"
            element={
              <GlobalAdminRoute>
                <LazyRoute>
                  <WorkflowBuilder />
                </LazyRoute>
              </GlobalAdminRoute>
            }
          />
          <Route
            path="workflow-actions"
            element={
              <SuperAdminRoute>
                <LazyRoute>
                  <WorkflowActionsAdmin />
                </LazyRoute>
              </SuperAdminRoute>
            }
          />
          <Route
            path="webhooks"
            element={
              <SuperAdminRoute>
                <LazyRoute>
                  <SendMessageWebhooksAdmin />
                </LazyRoute>
              </SuperAdminRoute>
            }
          />
          <Route path="conversations" element={<LazyRoute><ConversationsPage /></LazyRoute>} />
          <Route
            path="companyRegister"
            element={
              <SuperAdminRoute>
                <LazyRoute>
                  <CompanyRegister />
                </LazyRoute>
              </SuperAdminRoute>
            }
          />
          <Route
            path="client-updates"
            element={
              <GlobalAdminRoute>
                <LazyRoute>
                  <ClientUpdatesAdmin />
                </LazyRoute>
              </GlobalAdminRoute>
            }
          />
          <Route
            path="notices"
            element={
              <AdminRoute>
                <LazyRoute>
                  <NoticeBoard />
                </LazyRoute>
              </AdminRoute>
            }
          />
          <Route
            path="reports"
            element={
              <AdminRoute>
                <LazyRoute>
                  <Reports />
                </LazyRoute>
              </AdminRoute>
            }
          />
          <Route
            path="reports/conversations"
            element={
              <AdminRoute>
                <LazyRoute>
                  <ConversationAnalytics />
                </LazyRoute>
              </AdminRoute>
            }
          />
          <Route
            path="reports/messages"
            element={
              <AdminRoute>
                <LazyRoute>
                  <MessageTrafficAnalytics />
                </LazyRoute>
              </AdminRoute>
            }
          />
          <Route
            path="reports/satisfaction"
            element={
              <AdminRoute>
                <LazyRoute>
                  <SatisfactionAnalytics />
                </LazyRoute>
              </AdminRoute>
            }
          />
        </Route>

        {/* Página pública para o termo de responsabilidade */}
        <Route path="/whatsapp-terms" element={<WhatsAppTerms />} />

        {/* Rota padrão: redireciona para login */}
        <Route path="*" element={<Login />} />
      </Routes>
    </BrowserRouter>
  );
}
